import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { MessageCircle, Send, User } from 'lucide-react';

interface Message {
  id: string;
  sender_email: string;
  sender_name: string;
  content: string;
  created_at: string;
}

const AnonymousChatApp: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [username, setUsername] = useState('');
  const [isJoined, setIsJoined] = useState(false);

  // 載入訊息
  const loadMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('simple_messages_2025_10_24_04_43')
        .select('*')
        .order('created_at', { ascending: true })
        .limit(50);

      if (error) throw error;
      setMessages(data || []);
    } catch (error: any) {
      console.error('載入訊息失敗:', error);
    }
  };

  // 發送訊息
  const sendMessage = async () => {
    if (!newMessage.trim() || !username.trim()) return;

    try {
      const { error } = await supabase
        .from('simple_messages_2025_10_24_04_43')
        .insert({
          sender_email: `${username}@anonymous.com`,
          sender_name: username,
          content: newMessage.trim()
        });

      if (error) throw error;
      setNewMessage('');
    } catch (error: any) {
      toast({
        title: "發送失敗",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // 加入聊天室
  const joinChat = () => {
    if (!username.trim()) {
      toast({
        title: "請輸入用戶名",
        variant: "destructive",
      });
      return;
    }
    setIsJoined(true);
    toast({
      title: "歡迎加入聊天室",
      description: `歡迎 ${username}！`,
    });
  };

  useEffect(() => {
    if (isJoined) {
      loadMessages();
      
      // 訂閱新訊息
      const subscription = supabase
        .channel('anonymous_messages')
        .on('postgres_changes', 
          { event: 'INSERT', schema: 'public', table: 'simple_messages_2025_10_24_04_43' },
          (payload) => {
            setMessages(prev => [...prev, payload.new as Message]);
          }
        )
        .subscribe();

      return () => subscription.unsubscribe();
    }
  }, [isJoined]);

  if (!isJoined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-center gap-2">
              <MessageCircle className="h-6 w-6 text-blue-600" />
              匿名聊天室
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-gray-600 mb-4">
              無需註冊，直接開始聊天！
            </div>
            
            <Input
              placeholder="請輸入您的用戶名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && joinChat()}
            />
            
            <Button className="w-full" onClick={joinChat}>
              <User className="h-4 w-4 mr-2" />
              加入聊天室
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* 標題欄 */}
      <div className="bg-white border-b p-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-6 w-6 text-blue-600" />
          <h1 className="text-xl font-semibold">匿名聊天室</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">歡迎 {username}</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              setIsJoined(false);
              setUsername('');
              setMessages([]);
            }}
          >
            離開
          </Button>
        </div>
      </div>

      {/* 訊息區域 */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              還沒有訊息，開始聊天吧！
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender_name === username ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.sender_name === username
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border shadow-sm'
                  }`}
                >
                  {message.sender_name !== username && (
                    <div className="text-xs text-gray-500 mb-1">
                      {message.sender_name}
                    </div>
                  )}
                  <div>{message.content}</div>
                  <div className={`text-xs mt-1 ${
                    message.sender_name === username ? 'text-blue-100' : 'text-gray-400'
                  }`}>
                    {new Date(message.created_at).toLocaleTimeString('zh-TW', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* 輸入區域 */}
      <div className="bg-white border-t p-4 shadow-sm">
        <div className="flex gap-2">
          <Input
            placeholder="輸入訊息..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            className="flex-1"
          />
          <Button onClick={sendMessage} disabled={!newMessage.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnonymousChatApp;